import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM, markStagger } from '../motion';
import type { ChartSpec, ChartData } from '../copilotTypes';

type BubbleSpec = Extract<ChartSpec, { chart: 'bubble' }>;
type BubbleData = Extract<ChartData, { chart: 'bubble' }>;

// Bubble radius range, in pixels. Smallest `size` maps to MIN, largest to MAX; everything else lerps
// between them so relative magnitudes stay readable without any single bubble swamping the plot.
const SYMBOL_MIN = 6;
const SYMBOL_MAX = 28;

export function buildBubbleOption(spec: BubbleSpec, data: BubbleData) {
  const { points, units } = data;
  const sizes = points.map((p) => p.size);
  const sizeMin = Math.min(...sizes);
  const sizeMax = Math.max(...sizes);
  const sizeSpan = sizeMax - sizeMin;
  // When every point shares the same size value there's no spread to map, so a flat mid-range radius
  // keeps the bubbles uniform and legible instead of collapsing them all to the minimum.
  const symbolSize = (size: number) =>
    sizeSpan === 0 ? (SYMBOL_MIN + SYMBOL_MAX) / 2 : SYMBOL_MIN + ((size - sizeMin) / sizeSpan) * (SYMBOL_MAX - SYMBOL_MIN);

  // Carry x/y/size on each datum: x/y drive the scatter position, size drives symbolSize and the tooltip.
  const seriesData = points.map((p) => ({ value: [p.x, p.y], size: p.size }));

  return {
    ...ECHARTS_ANIM,
    // top:36 reserves a band above the plot for the y-axis unit (rendered at the axis 'end', top-left)
    // so it isn't cropped against the title; bottom:48 leaves room for the centered x-axis unit.
    grid: { left: 4, right: 16, top: 36, bottom: 48, containLabel: true },
    tooltip: {
      trigger: 'item',
      // Tooltip mirrors the histogram/calendar: a header line naming the point (its date), then the
      // bold measured values — x, y, and size — each paired with its metric unit.
      formatter: (p: any) => {
        const [x, y] = p.value;
        const size = p.data.size;
        const date = p.dataIndex != null ? points[p.dataIndex].date : '';
        return (
          `${date}<br/>` +
          `x: <strong>${x.toLocaleString()}</strong> ${units.x}<br/>` +
          `y: <strong>${y.toLocaleString()}</strong> ${units.y}<br/>` +
          `size: <strong>${size.toLocaleString()}</strong> ${units.size}`
        );
      },
    },
    xAxis: {
      type: 'value',
      name: units.x,
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
      scale: true,
      splitLine: { lineStyle: { color: tokens.surface.grid } },
    },
    yAxis: {
      type: 'value',
      name: units.y,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
      scale: true,
      splitLine: { lineStyle: { color: tokens.surface.grid } },
    },
    series: [
      {
        type: 'scatter',
        data: seriesData,
        symbolSize: (_value: any, params: any) => symbolSize(params.data.size),
        // Slightly translucent so overlapping bubbles stay separable when the cloud is dense.
        itemStyle: { opacity: 0.6 },
        // Bubbles appear in a single staggered sweep when sparse, all at once when dense (n>80). `n` is
        // the bubble count captured here, not read from the ECharts callback.
        animationDelay: markStagger(seriesData.length),
      },
    ],
  };
}

// memoized so a streaming re-render doesn't rebuild the option -> setOption(notMerge) reflow (see Histogram).
export const Bubble = React.memo(function Bubble({ title, spec, data }: { title: string; spec: BubbleSpec; data: BubbleData }) {
  const option = React.useMemo(() => buildBubbleOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
